import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const CVContainer = styled.div`
  padding: 8rem 2rem 6rem;
  min-height: calc(100vh - 14rem); /* Ajusté pour la navigation et le footer sur desktop */
  
  @media (max-width: 768px) {
    padding: 7rem 1.5rem 3rem; /* Réduction du padding sur mobile */
    min-height: auto; /* Permet au contenu de définir la hauteur naturelle */
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const CVCard = styled(motion.div)`
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const CVDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 700px;
`;

const DownloadButton = styled(motion.a)`
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  
  svg {
    margin-right: 1rem;
    width: 24px;
    height: 24px;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const PreviewCard = styled(motion.div)`
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
  position: relative;
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1.4; /* Ratio approximatif pour un document A4 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    display: none; /* Cache la prévisualisation sur mobile */
  }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none; /* Cache la prévisualisation sur mobile en orientation horizontale */
  }
  
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    display: none; /* Cache la prévisualisation sur tablette en orientation horizontale */
  }
`;

const SafeIframe = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 1000px; /* Hauteur minimale pour bien afficher une page A4 */
  border: none;
  border-radius: 8px;
`;

const CVPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  
  // Détecte si l'appareil est mobile au chargement et lors du redimensionnement
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Vérification initiale
    checkMobile();
    
    // Écouter les changements de taille d'écran
    window.addEventListener('resize', checkMobile);
    
    // Nettoyage
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Déterminer le chemin du CV en fonction de la langue
  const getResumePath = () => {
    const language = i18n.language || 'fr';
    const filename = t('cv:filename');
    return `/documents/${filename}`;
  };
  
  // Chemin du CV (mis à jour automatiquement si la langue change)
  const resumePath = getResumePath();

  return (
    <CVContainer>
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t('cv:pageTitle')}
      </SectionTitle>
      
      <ContentWrapper>
        <CVCard
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <CVDescription>
            {t('cv:description')}
          </CVDescription>
          
          <DownloadButton 
            href={resumePath}
            download={t('cv:filename')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
            </svg>
            {t('cv:downloadButton')}
          </DownloadButton>
        </CVCard>
        
        {/* Prévisualisation du PDF (uniquement sur desktop) */}
        {!isMobile && (
          <PreviewCard
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <SafeIframe 
              title="CV Preview" 
              src={`${resumePath}?v=${Date.now()}#toolbar=0`}
            />
          </PreviewCard>
        )}
      </ContentWrapper>
    </CVContainer>
  );
};

export default CVPage;